import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

/** Live presence for wallets we have heard about via `presence:changed` (server edge events). */
export interface PresenceEntry {
  online: boolean
  /** Server unix ms of the transition. */
  at: number
}

export interface PresenceState {
  /** lowercase wallet -> last known state. Only wallets with a received edge are present. */
  byWallet: Record<string, PresenceEntry>
  /** Bumped on every accepted edge; consumers that refetch lists (chat online users) watch this. */
  version: number
  /** Our own presence socket: connected + last ack. */
  selfConnected: boolean
  selfLastAckAt: number | null
  /** Last server-reported reason a beat was refused (`unauthenticated`, `blocked`, …). */
  selfLastRefusal: string | null
}

const initialState: PresenceState = {
  byWallet: {},
  version: 0,
  selfConnected: false,
  selfLastAckAt: null,
  selfLastRefusal: null,
}

export const presenceSlice = createSlice({
  name: 'presence',
  initialState,
  reducers: {
    presenceChanged: (
      state: PresenceState,
      action: PayloadAction<{ walletAddress: string; online: boolean; at?: number }>,
    ) => {
      const w = String(action.payload.walletAddress || '')
        .toLowerCase()
        .trim()
      if (!w) return
      const at = Number(action.payload.at) || Date.now()
      const prev = state.byWallet[w]
      // Ignore out-of-order edges (worker reaper vs API beat can race by a few ms).
      if (prev && prev.at > at) return
      state.byWallet[w] = { online: !!action.payload.online, at }
      state.version += 1
    },
    presenceSelfConnection: (state: PresenceState, action: PayloadAction<boolean>) => {
      state.selfConnected = action.payload
    },
    presenceSelfAck: (
      state: PresenceState,
      action: PayloadAction<{ ok: boolean; reason?: string | null }>,
    ) => {
      state.selfLastAckAt = Date.now()
      state.selfLastRefusal = action.payload.ok ? null : action.payload.reason || 'refused'
    },
    presenceReset: () => initialState,
  },
})

export const { presenceChanged, presenceSelfConnection, presenceSelfAck, presenceReset } =
  presenceSlice.actions
export default presenceSlice.reducer
